import { FormEvent, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Cloud, KeyRound } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";

import OneDriveIcon from "@/assets/file-icons/onedrive.svg";
import { useDebounce } from "@/hooks/useDebounce";
import { useRecentCloudLocations } from "@/hooks/useRecentCloudLocations";
import {
  requestSharePointSitesAccess,
  SITES_ACCESS_QUERY_KEY,
  useSharePointSiteSearch,
  useSharePointSitesAccess,
} from "@/hooks/useSharePointSiteSearch";
import {
  normalizeSharePointSiteUrl,
  SharePointSiteUrlRejection,
} from "@/lib/sharePointSiteUrl";
import {
  CloudLocation,
  ONE_DRIVE_LOCATION,
  SharePointSite,
} from "@/models/microsoft/CloudLocation";
import { SharePointSitesSearchError } from "@/services/microsoft/sharePointSitesService";
import { getDomain, getDomainName } from "@/utils/domain";

export type CloudLocationPickerDialogProps = {
  isOpen: boolean;
  /** The user dismissed the dialog without picking (Escape, overlay, close). */
  onClose: () => void;
  /**
   * Called synchronously in the user's click. The owner is expected to close
   * the dialog (set `isOpen` false) in response; `onClose` is not called.
   */
  onSelect: (location: CloudLocation) => void;
};

const URL_ERROR_TEXT: Record<SharePointSiteUrlRejection, string> = {
  invalid: "Indtast en gyldig adresse (https://...)",
  notSharePoint: "Adressen skal være et SharePoint-site (…sharepoint.com)",
  oneDrive: "Det er et OneDrive-link. Vælg 'Min OneDrive' i stedet.",
};

const sitePath = (webUrl: string) => {
  try {
    const url = new URL(webUrl);
    return `${url.hostname}${url.pathname === "/" ? "" : url.pathname}`;
  } catch {
    return webUrl;
  }
};

const SiteCommandItem = ({
  site,
  onSelect,
}: {
  site: SharePointSite;
  onSelect: (site: SharePointSite) => void;
}) => (
  <CommandItem value={site.webUrl} onSelect={() => onSelect(site)}>
    <Building2 className="size-4 mr-2 shrink-0" />
    <div className="flex flex-col items-start min-w-0">
      <span className="text-sm truncate max-w-full">{site.displayName}</span>
      <span className="text-xs text-muted-foreground truncate max-w-full">
        {sitePath(site.webUrl)}
      </span>
    </div>
  </CommandItem>
);

/**
 * "Vælg placering": where the Microsoft File Picker should open. Min OneDrive
 * (today's picker), the user's recent SharePoint sites, Graph site search, or
 * a pasted site link. Search needs the Graph scope Sites.Read.All; until the
 * user (or their tenant admin) has consented, the dialog offers "Aktivér
 * søgning" and falls back to recents and paste-a-link.
 */
export const CloudLocationPickerDialog = ({
  isOpen,
  onClose,
  onSelect,
}: CloudLocationPickerDialogProps) => {
  const queryClient = useQueryClient();
  // "Lara" or "Loda" depending on the host; never mention the other brand.
  const brandName = getDomainName(getDomain(window.location.host));
  const [searchInput, setSearchInput] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const [accessRequestFailed, setAccessRequestFailed] = useState(false);

  const searchQuery = useDebounce(searchInput.trim(), 300);
  const access = useSharePointSitesAccess(isOpen);
  const hasAccess = access.data === true;
  const search = useSharePointSiteSearch(searchQuery, hasAccess);
  const recent = useRecentCloudLocations(isOpen);

  const searchDenied =
    search.error instanceof SharePointSitesSearchError &&
    search.error.isAccessDenied;
  const searchAvailable = hasAccess && !searchDenied;
  const isSearching = searchAvailable && searchQuery.length >= 2;

  useEffect(() => {
    if (isOpen) return;
    setSearchInput("");
    setUrlInput("");
    setUrlError(null);
    setAccessRequestFailed(false);
  }, [isOpen]);

  // A pick is reported only via onSelect; the owner closes the dialog by
  // clearing its state. Calling onClose here too would run the owner's
  // dismiss/cancel path for a successful selection.
  const select = (location: CloudLocation) => onSelect(location);

  const selectSite = (site: SharePointSite) =>
    select({ kind: "site", ...site });

  const handleSubmitUrl = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeSharePointSiteUrl(urlInput);
    if (!result.ok) {
      setUrlError(URL_ERROR_TEXT[result.reason]);
      return;
    }
    setUrlError(null);
    // A pasted library/folder link opens the picker right there; recents stay
    // at site level because the provider remembers only webUrl + displayName.
    select({
      kind: "site",
      webUrl: result.webUrl,
      displayName: result.displayName,
      list: result.list,
      folder: result.folder,
    });
  };

  const handleRequestAccess = async () => {
    setIsRequestingAccess(true);
    try {
      const granted = await requestSharePointSitesAccess();
      setAccessRequestFailed(!granted);
      if (granted) {
        queryClient.setQueryData(SITES_ACCESS_QUERY_KEY, true);
      }
    } finally {
      setIsRequestingAccess(false);
    }
  };

  const renderSearchResults = () => {
    if (search.isLoading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner className="size-5" />
        </div>
      );
    }
    if (search.isError) {
      return (
        <CommandEmpty>
          Søgningen fejlede. Prøv igen eller indsæt et link nedenfor.
        </CommandEmpty>
      );
    }
    return (
      <>
        <CommandEmpty>Ingen sites fundet.</CommandEmpty>
        <CommandGroup heading="Søgeresultater">
          {(search.data ?? []).map((site) => (
            <SiteCommandItem
              key={site.webUrl}
              site={site}
              onSelect={selectSite}
            />
          ))}
        </CommandGroup>
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="size-5" />
            Vælg placering
          </DialogTitle>
          <DialogDescription>
            Vælg din OneDrive eller et SharePoint-site at hente filer fra.
          </DialogDescription>
        </DialogHeader>

        <Command className="border rounded-md" shouldFilter={false}>
          {searchAvailable && (
            // Mounts after the silent consent probe resolves, so the dialog's
            // initial focus has already landed on the URL field: pull it here.
            <CommandInput
              autoFocus
              placeholder="Søg efter SharePoint-sites..."
              value={searchInput}
              onValueChange={setSearchInput}
            />
          )}
          <CommandList className="max-h-[280px]">
            {!isSearching && (
              <CommandGroup heading="Placering">
                <CommandItem
                  value="onedrive"
                  onSelect={() => select(ONE_DRIVE_LOCATION)}
                >
                  <img src={OneDriveIcon} className="size-4 mr-2" alt="" />
                  Min OneDrive
                </CommandItem>
              </CommandGroup>
            )}

            {!isSearching && recent.sites.length > 0 && (
              <CommandGroup heading="Seneste placeringer">
                {recent.sites.map((site) => (
                  <SiteCommandItem
                    key={site.webUrl}
                    site={site}
                    onSelect={selectSite}
                  />
                ))}
              </CommandGroup>
            )}

            {isSearching && renderSearchResults()}
          </CommandList>
        </Command>

        {!searchAvailable && !access.isLoading && (
          <Alert>
            <AlertDescription className="flex flex-col gap-2">
              <span>
                Søgning efter SharePoint-sites kræver, at du giver {brandName}{" "}
                adgang til at læse dine sites (Sites.Read.All).
                {accessRequestFailed &&
                  " Kræver din organisation godkendelse, skal jeres IT-administrator godkende rettigheden først. Du kan i stedet indsætte linket til dit SharePoint-site nedenfor."}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={handleRequestAccess}
                disabled={isRequestingAccess}
              >
                {isRequestingAccess ? (
                  <Spinner className="size-4" />
                ) : (
                  <KeyRound className="size-4 mr-2" />
                )}
                Aktivér søgning
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmitUrl} className="space-y-2">
          <Label htmlFor="cloud-location-url">
            Eller indsæt link til et SharePoint-site
          </Label>
          <div className="flex gap-2">
            {/* A paste-to-navigate box, not form data: the picker is an Info dialog (LAR-214). */}
            {/* eslint-disable-next-line local/no-input-outside-form-dialog */}
            <Input
              id="cloud-location-url"
              placeholder="https://firma.sharepoint.com/sites/..."
              value={urlInput}
              onChange={(event) => {
                setUrlInput(event.target.value);
                setUrlError(null);
              }}
              aria-invalid={urlError !== null}
            />
            <Button
              type="submit"
              variant="outline"
              disabled={urlInput.trim() === ""}
            >
              Åbn
            </Button>
          </div>
          {urlError && <p className="text-sm text-destructive">{urlError}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
};
